/** User-scoped API client. No business data is persisted in browser storage. */
export class ProductClient {
  constructor(getToken, fetcher = (...args) => globalThis.fetch(...args)) {
    this.getToken = getToken;
    this.fetcher = fetcher;
    this.controllers = new Set();
    this.disposed = false;
  }
  dispose() {
    this.disposed = true;
    for (const controller of this.controllers) controller.abort();
    this.controllers.clear();
  }
  async request(path, options = {}) {
    return this.perform(path, options, async (response) =>
      response.status === 204 ? null : response.json(),
    );
  }
  async download(path, options = {}) {
    return this.perform(path, options, (response) => response.blob());
  }
  async stream(path, body, onEvent, options = {}) {
    return this.perform(
      path,
      { method: "POST", body, signal: options.signal },
      (response) => consumeSSE(response.body, onEvent),
    );
  }
  async perform(path, options, consume) {
    if (this.disposed) throw new Error("请重新登录");
    let token = await this.getToken();
    if (!token || this.disposed) throw new Error("请重新登录");
    const controller = new AbortController();
    this.controllers.add(controller);
    const abort = () => controller.abort();
    if (options.signal?.aborted) controller.abort();
    options.signal?.addEventListener("abort", abort);
    try {
      const send = () => this.fetcher(`/api/v1${path}`, {
        method: options.method || "GET",
        headers: {
          ...(options.headers || {}),
          Authorization: `Bearer ${token}`,
          ...(options.body === undefined
            ? {}
            : { "Content-Type": "application/json" }),
        },
        body:
          options.body === undefined ? undefined : JSON.stringify(options.body),
        credentials: "same-origin",
        cache: "no-store",
        signal: controller.signal,
      });
      let response = await send();
      if (response.status === 401 && !controller.signal.aborted && !this.disposed) {
        await response.body?.cancel();
        token = await this.getToken(true);
        if (!token || this.disposed) throw new Error("请重新登录");
        response = await send();
      }
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const detail = payload.error || payload.detail || payload;
        const error = new Error(
          typeof detail === "string"
            ? detail
            : detail.message || "请求未完成，请稍后重试",
        );
        error.code = detail.code || "request_failed";
        error.status = response.status;
        throw error;
      }
      return await consume(response);
    } finally {
      options.signal?.removeEventListener("abort", abort);
      this.controllers.delete(controller);
    }
  }
}

export async function consumeSSE(body, onEvent) {
  if (!body) throw new Error("连接中断，请检查回复状态");
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let pending = "",
    completed = false;
  const dispatch = (frame) => {
    const data = frame
      .split(/\r?\n/)
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).trimStart())
      .join("\n");
    if (!data || data === "[DONE]") return;
    const event = JSON.parse(data);
    // Explicit allowlist keeps internal reasoning and diagnostics out of UI state.
    if (
      ![
        "response.created",
        "response.text.delta",
        "response.completed",
        "response.cancelled",
        "error",
      ].includes(event.type)
    )
      return;
    if (event.type === "error" || event.type === "response.cancelled") {
      const error = new Error(event.message || "回复未完成");
      error.code = event.code || "turn_cancelled";
      error.turn_id = event.turn_id;
      error.status =
        event.status ||
        (event.type === "response.cancelled" ? "cancelled" : undefined);
      throw error;
    }
    if (event.type === "response.completed") completed = true;
    onEvent(event);
  };
  try {
    while (true) {
      const { value, done } = await reader.read();
      pending += decoder.decode(value, { stream: !done });
      let match;
      while ((match = /\r?\n\r?\n/.exec(pending))) {
        dispatch(pending.slice(0, match.index));
        pending = pending.slice(match.index + match[0].length);
      }
      if (done) break;
    }
    if (pending.trim()) dispatch(pending);
    if (!completed) throw new Error("连接中断，请检查回复状态后继续");
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}
